import {
  AssetRoutineState,
  RoutineState,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import { useMemo } from 'react';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import { tokens } from '../../../crop-circle/tokens';
import { useFormContext } from '../../../forms';
import { isIdpManagedQuestion } from '../admin/idpQuestions';
import { stylesCropCircle } from './styles';
import { WorkflowExecutionQuestion } from './workflowExecutionQuestion';

type WorkflowExecutionPageProps = {
  page: WorkflowPageConfig;
  workspaceAsset: WorkspaceAsset;
  toggleProcessing?: (value: boolean) => void;
  currentState: AssetRoutineState<RoutineState> | undefined;
  isIssueEditRoutine?: boolean;
  idpEnabled?: boolean;
};

interface QuestionResponse {
  index: number;
  config: WorkflowQuestionConfig;
}

export const WorkflowExecutionPage = (props: WorkflowExecutionPageProps) => {
  const { getValues } = useFormContext();
  const fields = getValues('responses');

  // IDP-managed questions are the AI's job, not the operator's — hide them on
  // initial execution only. On the revise/edit path (isIssueEditRoutine) a
  // reviewer must still see them to correct AI-filled values after extraction
  // completes (plan finding #14). `idpManaged` survives an IDP off-toggle, so
  // this only actually hides while the routine's IDP toggle is on (mirrors the
  // admin-side `isLockedIdpSource` convention). The response slot itself keeps
  // seeding in workflowExecutionForm.tsx regardless — this filter is render-only.
  const hideIdpManagedQuestions = !!props.idpEnabled && !props.isIssueEditRoutine;

  const questionResponses = useMemo<QuestionResponse[]>(
    () =>
      [...props.page.questions]
        .sort((a, b) => a.order - b.order)
        .filter(
          (question) =>
            !hideIdpManagedQuestions || !isIdpManagedQuestion(question)
        )
        .map((question) => ({
          index: fields.findIndex(
            (field: any) => field.questionId === question.id
          ),
          config: question,
        })),
    [props.page.questions, fields, hideIdpManagedQuestions]
  );

  return (
    <>
      {props.page.instructions && (
        <View style={stylesCropCircle.instructionsContainer}>
          <Text style={tokens.typography.body.mediumEmphasised}>
            {props.page.instructions}
          </Text>
        </View>
      )}
      {questionResponses.map((response, index) => (
        <WorkflowExecutionQuestion
          key={`question_${index}`}
          config={response.config}
          questionIndex={index}
          responseIndex={response.index}
          workspaceAsset={props.workspaceAsset}
          toggleProcessing={props.toggleProcessing}
          currentState={props.currentState}
          isIssueEditRoutine={props.isIssueEditRoutine}
          idpEnabled={props.idpEnabled}
        />
      ))}
    </>
  );
};
